import React, { Component } from "react";
import { connect } from "react-redux";
import { setModals } from '../../redux/actions';
import { Modal } from 'reactstrap';
import { getValueOfParameterFromURL, generateGUID } from '../../helpers/Utils';

const MODAL_RIGHT_WIDTHS = {
  sm: 300,
  md: 500,
  lg: 800,
  xl: 940
}
const NESTED_RIGHT_MODAL_SHRINK_PX = 15

// Tracks currently open right-side modals for nested width (separate from history stack)
const openRightModals = []

const reapplyBodyScrollbarPadding = () => {
  if (!document.body.classList.contains('modal-open')) return
  const probe = document.createElement('div')
  probe.style.cssText = 'position:absolute;top:-9999px;width:50px;height:50px;overflow:scroll'
  document.body.appendChild(probe)
  const scrollbarWidth = probe.offsetWidth - probe.clientWidth
  document.body.removeChild(probe)
  document.body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : null
}

class CustomModal extends Component {
  constructor(props) {
    super(props)
    this.state = {
      modalId: null,
      clickedBackButton: false,
      currentHash: window.location.hash.split("?")[0]?? ""
    }
    this.rightStackId = generateGUID()
    this._nestedWidthStyle = null
  }

  isModalRight = () => {
    const { wrapClassName } = this.props
    return typeof wrapClassName === 'string' && wrapClassName.split(/\s+/).includes('modal-right')
  }

  getConfiguredWidth = () => {
    return MODAL_RIGHT_WIDTHS[this.props.size] || MODAL_RIGHT_WIDTHS.lg
  }

  registerRightModal = () => {
    if (!this.isModalRight()) return
    if (openRightModals.findIndex(m => m.id === this.rightStackId) === -1) {
      openRightModals.push({
        id: this.rightStackId,
        width: this.getConfiguredWidth()
      })
      this.forceUpdate()
    }
  }

  unregisterRightModal = () => {
    const index = openRightModals.findIndex(m => m.id === this.rightStackId)
    if (index > -1) {
      openRightModals.splice(index, 1)
    }
    this._nestedWidthStyle = null
  }

  componentDidMount () {
  }

  componentDidUpdate(prevProps) {
    const handle = () => {
      if (this.props.isOpen !== prevProps.isOpen) {
        if (this.props.isOpen) {
          window.location.hash = `${this.state.currentHash}?modalId=${this.state.modalId}`
        }
        else {
          const id = getValueOfParameterFromURL("modalId", window.location.href)
          if (id && this.state.clickedBackButton === false) {
            window.history.go(-1);
          }
        }
      }
    }
    if (this.props.isOpen && this.state.modalId === null) {
      const id = generateGUID()
      this.setState({modalId: id}, () => {
        let modals = this.props.modals
        modals.push(this.state.modalId)
        this.props.setModals(modals)
        window.addEventListener('popstate', this.handleUrlChange)
        handle()
      })
    } else handle()

    if (this.props.isOpen && !prevProps.isOpen) {
      this.registerRightModal()
    }
    // Unregister is deferred to onClosed so width/stack stay stable during the exit animation
  }

  componentWillUnmount () {
    window.removeEventListener('popstate', this.handleUrlChange)
    this.unregisterRightModal()
  }

  handleUrlChange = () => {
    let modals = this.props.modals
    const id = getValueOfParameterFromURL("modalId", window.location.href)
    const isLast = this.props.modals[this.props.modals.length - 1] === this.state.modalId
    if (this.props.isOpen && this.state.modalId !== id && (isLast || id === null || this.props.modals.indexOf(id) === -1)) {
      this.setState({clickedBackButton: true}, () => {
        modals.pop(this.state.modalId)
        this.props.setModals(modals)
        this.props.toggle()
      })
    } else this.setState({clickedBackButton: false})
  }

  handleClosed = () => {
    this.unregisterRightModal()
    // reactstrap always resets body padding on destroy; re-apply if another modal remains
    reapplyBodyScrollbarPadding()
    if (this.props.onClosed) this.props.onClosed()
  }

  getNestedRightModalStyle = (style) => {
    if (!this.isModalRight()) return style

    // Keep last nested width while closing so the panel doesn't jump wider over the one behind
    if (!this.props.isOpen) {
      return this._nestedWidthStyle ? { ...style, ...this._nestedWidthStyle } : style
    }

    let stackIndex = openRightModals.findIndex(m => m.id === this.rightStackId)
    // First paint can happen before didUpdate registers this id
    if (stackIndex === -1) stackIndex = openRightModals.length
    if (stackIndex <= 0) {
      this._nestedWidthStyle = null
      return style
    }

    // Keep full-width behavior on small screens (matches .modal-right .modal-lg below sm)
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      this._nestedWidthStyle = null
      return style
    }

    // Nested modal = width of the modal underneath, minus 15px (ignore own size like md)
    const parent = openRightModals[stackIndex - 1]
    const parentWidth = parent?.width || this.getConfiguredWidth()
    const width = parentWidth - NESTED_RIGHT_MODAL_SHRINK_PX

    // Keep stack entry in sync with the effective nested width for deeper nesting
    const self = openRightModals[stackIndex]
    if (self) self.width = width

    this._nestedWidthStyle = {
      minWidth: `${width}px`,
      maxWidth: `${width}px`,
      width: `${width}px`
    }

    return {
      ...style,
      ...this._nestedWidthStyle
    }
  }

  render() {
    const { setModals, modals, children, style, modalTransition, backdropTransition, onClosed, ...rest } = this.props
    return (
      <Modal
        {...rest}
        style={this.getNestedRightModalStyle(style)}
        modalTransition={modalTransition || { timeout: 100 }}
        backdropTransition={backdropTransition || { timeout: 80 }}
        onClosed={this.handleClosed}
      >
        {children}
      </Modal>
    )
  }
}

const mapStateToProps = ({ utils }) => {
  const { modals } = utils
  return { modals }
}

export default connect(mapStateToProps, { setModals })(CustomModal)
